import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowLeftRight, LogOut } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { CompanySwitcher } from '@/components/auth/CompanySwitcher';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';

function initials(firstname?: string | null, lastname?: string | null, email?: string): string {
  if (firstname?.trim() || lastname?.trim()) {
    const f = (firstname?.trim() ?? '').charAt(0).toUpperCase();
    const l = (lastname?.trim() ?? '').charAt(0).toUpperCase();
    return ((f + l).trim() || email?.charAt(0)?.toUpperCase()) ?? '?';
  }
  return email?.charAt(0)?.toUpperCase() ?? '?';
}

function getDaysInCurrentMonth(): { date: string; day: number; tokens: number }[] {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const rows: { date: string; day: number; tokens: number }[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    rows.push({
      date: date.toISOString().slice(0, 10),
      day: d,
      tokens: 0,
    });
  }
  return rows;
}

export function SettingsView() {
  const { user, selectedEmployer } = useAuth();
  const navigate = useNavigate();
  const [companyOpen, setCompanyOpen] = useState(false);

  const displayName = [user?.firstname, user?.lastname].filter(Boolean).join(' ') || user?.email;
  const employerDisplay =
    selectedEmployer?.shortname ?? selectedEmployer?.name ?? selectedEmployer?.code ?? '—';

  const totalTokens = user?.aiUsage?.totalTokens ?? 0;
  const limit = user?.aiUsage?.limit ?? 3_000_000;
  const usagePercent = limit > 0 ? Math.min(100, (totalTokens / limit) * 100) : 0;

  const chartData = (() => {
    const days = getDaysInCurrentMonth();
    const byDate = new Map((user?.aiUsage?.dailyUsage ?? []).map((u) => [u.date, u.tokens]));
    return days.map((row) => ({
      ...row,
      tokens: byDate.get(row.date) ?? 0,
      label: `Day ${row.day}`,
    }));
  })();

  return (
    <div className="flex h-full flex-col overflow-auto p-6">
      <div className="mx-auto w-full max-w-2xl space-y-6">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground">Your account, company, and AI usage.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>AI usage</CardTitle>
            <CardDescription>
              Token usage for AI chat and features. Limit: 3M tokens.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-baseline justify-between gap-2">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold tabular-nums">
                  {totalTokens.toLocaleString()}
                </span>
                <span className="text-sm text-muted-foreground">
                  / {limit.toLocaleString()} tokens
                </span>
              </div>
              <span className="text-sm tabular-nums text-muted-foreground">
                {usagePercent.toFixed(1)}% used
              </span>
            </div>
            <Progress value={usagePercent} className="h-3" />
            <p className="text-sm text-muted-foreground">
              Usage for your account{selectedEmployer ? ` (${employerDisplay})` : ''} — current
              month below.
            </p>

            {chartData.some((d) => d.tokens > 0) && (
              <div className="mt-6">
                <h4 className="mb-3 text-sm font-medium">Usage by day (current month)</h4>
                <div className="h-48 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => `${d}`} />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                      />
                      <Tooltip
                        formatter={(value) => [
                          value != null ? Number(value).toLocaleString() : '—',
                          'Tokens',
                        ]}
                        labelFormatter={(_, payload) =>
                          payload[0]?.payload?.date
                            ? new Date(payload[0].payload.date).toLocaleDateString()
                            : ''
                        }
                      />
                      <Bar dataKey="tokens" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
            <CardDescription>Current user and company</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-medium text-primary-foreground"
                aria-hidden
              >
                {initials(user?.firstname, user?.lastname, user?.email)}
              </div>
              <div className="min-w-0 space-y-1">
                <p className="truncate font-medium leading-tight">{displayName}</p>
                <p className="truncate text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <Separator />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="mb-1 text-sm text-muted-foreground">Company</p>
                <p className="font-medium">
                  {selectedEmployer?.name ?? '—'}
                  {selectedEmployer?.code && (
                    <span className="ml-2 font-normal text-muted-foreground">
                      ({selectedEmployer.code})
                    </span>
                  )}
                </p>
                {selectedEmployer?.shortname &&
                  selectedEmployer.shortname !== selectedEmployer.name && (
                    <p className="text-sm text-muted-foreground">
                      Short name: {selectedEmployer.shortname}
                    </p>
                  )}
              </div>
              <Button variant="outline" size="sm" onClick={() => setCompanyOpen(true)}>
                <ArrowLeftRight className="mr-2 h-4 w-4" />
                Switch company
              </Button>
            </div>
            <Separator />
            <Button
              variant="outline"
              className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => navigate('/logout')}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </Button>
          </CardContent>
        </Card>
      </div>

      <CompanySwitcher open={companyOpen} onOpenChange={setCompanyOpen} />
    </div>
  );
}
